import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'

interface Evidence {
  id: string
  title: string
  evidence_type: 'file' | 'link' | 'commit' | 'activity'
  path: string | null
  relationship: 'contributes' | 'completes'
  status: 'suggested' | 'confirmed' | 'rejected'
  ai_interpretation: string | null
  ai_suggestion_reason: string | null
  ai_confidence: number | null
  added_by: { id: string; name: string } | null
  added_at: string | null
}

interface DeliverableBase {
  id: string
  title: string
  description?: string | null
  due_date: string | null
  status: 'not_started' | 'in_progress' | 'complete'
  verification?: string | null
  ai_summary: string | null
  ai_confidence: number | null
  ai_assessed_at: string | null
  ai_suggests_complete: boolean
  evidence?: Evidence[]
  suggested_evidence?: Evidence[]
}

interface AssessmentResponse {
  assessment: {
    summary: string
    confidence: number
    suggests_complete: boolean
    next_steps: string[]
  }
  deliverable: DeliverableBase
}

interface DeliverableCardProps {
  deliverable: DeliverableBase
  type: 'personal' | 'project'
  onUpdate?: () => void
  showAddEvidence?: boolean
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

function EvidenceTypeIcon({ type }: { type: string }) {
  const icons: Record<string, string> = {
    file: '📄',
    link: '🔗',
    commit: '💻',
    activity: '📋',
  }
  return <span>{icons[type] || '📎'}</span>
}

function ConfidenceBar({ confidence }: { confidence: number | null }) {
  if (confidence === null) return null

  const color = confidence >= 75 ? 'bg-green-500' :
                confidence >= 50 ? 'bg-yellow-500' :
                confidence >= 25 ? 'bg-orange-500' : 'bg-red-500'

  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 bg-[var(--color-surface)] rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${confidence}%` }}
        />
      </div>
      <span className="text-xs text-[var(--color-text-secondary)]">{confidence}%</span>
    </div>
  )
}

export function DeliverableCard({ deliverable: d, type, onUpdate, showAddEvidence = true }: DeliverableCardProps) {
  const [showEvidence, setShowEvidence] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [isAssessing, setIsAssessing] = useState(false)
  const [nextSteps, setNextSteps] = useState<string[]>([])
  const [newEvidence, setNewEvidence] = useState({ title: '', path: '', evidence_type: 'file' as const, relationship: 'contributes' as const })

  const queryClient = useQueryClient()
  const endpoint = type === 'personal' ? 'deliverables' : 'project-deliverables'

  const hasEvidence = (d.evidence?.length || 0) > 0
  const hasSuggestions = (d.suggested_evidence?.length || 0) > 0

  // Add evidence mutation
  const addEvidenceMutation = useMutation({
    mutationFn: (data: typeof newEvidence) =>
      api.post(`/${endpoint}/${d.id}/evidence`, data),
    onSuccess: () => {
      queryClient.invalidateQueries()
      setShowAddForm(false)
      setNewEvidence({ title: '', path: '', evidence_type: 'file', relationship: 'contributes' })
      onUpdate?.()
    },
  })

  // Update evidence mutation (confirm/reject)
  const updateEvidenceMutation = useMutation({
    mutationFn: ({ evidenceId, status }: { evidenceId: string; status: string }) =>
      api.patch(`/evidence/${evidenceId}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries()
      onUpdate?.()
    },
  })

  // Remove/unlink activity mutation
  const removeEvidenceMutation = useMutation({
    mutationFn: (evidenceId: string) =>
      api.delete(`/evidence/${evidenceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries()
      onUpdate?.()
    },
  })

  // Assess mutation
  const assessMutation = useMutation({
    mutationFn: () => api.post<AssessmentResponse>(`/${endpoint}/${d.id}/assess`, {}),
    onSuccess: (data) => {
      queryClient.invalidateQueries()
      setIsAssessing(false)
      if (data.assessment?.next_steps) {
        setNextSteps(data.assessment.next_steps)
      }
      onUpdate?.()
    },
    onError: () => {
      setIsAssessing(false)
    },
  })

  const handleAssess = () => {
    setIsAssessing(true)
    assessMutation.mutate()
  }

  const handleConfirmEvidence = (evidenceId: string) => {
    updateEvidenceMutation.mutate({ evidenceId, status: 'confirmed' })
  }

  const handleRejectEvidence = (evidenceId: string) => {
    updateEvidenceMutation.mutate({ evidenceId, status: 'rejected' })
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{d.title}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              d.status === 'complete' ? 'bg-green-900/50 text-green-300' :
              d.status === 'in_progress' ? 'bg-blue-900/50 text-blue-300' :
              'bg-gray-800 text-gray-300'
            }`}>
              {d.status === 'not_started' ? 'Not Started' :
               d.status === 'in_progress' ? 'In Progress' : 'Complete'}
            </span>
            {d.ai_suggests_complete && d.status !== 'complete' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-900/50 text-purple-300">
                🤖 Suggests Complete
              </span>
            )}
          </div>
          {d.description && (
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              {d.description}
            </p>
          )}
          <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mt-2">
            {d.due_date && <span>Due: {formatDate(d.due_date)}</span>}
            {d.verification && <span>Verification: {d.verification}</span>}
          </div>
        </div>

        {/* AI Confidence */}
        {d.ai_confidence !== null && (
          <div className="ml-4">
            <ConfidenceBar confidence={d.ai_confidence} />
          </div>
        )}
      </div>

      {/* AI Summary */}
      {d.ai_summary && (
        <div className="mt-3 p-3 bg-purple-900/20 border border-purple-900/30 rounded">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <span>🤖</span>
              <span className="text-xs font-medium text-purple-300">AI Assessment</span>
              {d.ai_confidence !== null && (
                <span className="text-xs text-[var(--color-text-secondary)]">
                  ({d.ai_confidence}% confidence)
                </span>
              )}
            </div>
            <button
              onClick={handleAssess}
              disabled={isAssessing}
              className="text-xs text-purple-400 hover:text-purple-300"
            >
              {isAssessing ? 'Reassessing...' : 'Reassess'}
            </button>
          </div>
          <p className="text-sm">{d.ai_summary}</p>
          {nextSteps.length > 0 && (
            <div className="mt-2 pt-2 border-t border-purple-900/30">
              <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">Suggested next steps:</p>
              <ul className="text-sm space-y-1">
                {nextSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {d.ai_assessed_at && (
            <p className="text-xs text-[var(--color-text-secondary)] mt-2">
              Assessed {formatRelativeTime(d.ai_assessed_at)}
            </p>
          )}
        </div>
      )}

      {/* No assessment yet */}
      {!d.ai_summary && d.status !== 'complete' && (
        <div className="mt-3 p-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--color-text-secondary)]">
              No AI assessment yet
            </span>
            <button
              onClick={handleAssess}
              disabled={isAssessing}
              className="text-xs px-3 py-1 bg-purple-900/50 text-purple-300 rounded hover:bg-purple-900/70 disabled:opacity-50"
            >
              {isAssessing ? '🤖 Assessing...' : '🤖 Run Assessment'}
            </button>
          </div>
        </div>
      )}

      {/* Suggested Activity (needs review) */}
      {hasSuggestions && (
        <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-900/30 rounded">
          <div className="text-xs font-medium text-yellow-300 mb-2">
            ⚠ {d.suggested_evidence?.length} linked activity item{d.suggested_evidence?.length !== 1 ? 's' : ''} need review
          </div>
          <div className="space-y-2">
            {d.suggested_evidence?.map((e) => (
              <div key={e.id} className="flex items-center justify-between bg-[var(--color-surface)] p-2 rounded">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <EvidenceTypeIcon type={e.evidence_type} />
                  <span className="text-sm truncate">{e.title}</span>
                  {e.ai_confidence && (
                    <span className="text-xs text-[var(--color-text-secondary)]">
                      {Math.round(e.ai_confidence * 100)}%
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <button
                    onClick={() => handleConfirmEvidence(e.id)}
                    className="px-2 py-1 text-xs bg-green-900/50 text-green-300 rounded hover:bg-green-900/70"
                    disabled={updateEvidenceMutation.isPending}
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => handleRejectEvidence(e.id)}
                    className="px-2 py-1 text-xs bg-red-900/50 text-red-300 rounded hover:bg-red-900/70"
                    disabled={updateEvidenceMutation.isPending}
                  >
                    ✗
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Section */}
      {(hasEvidence || showAddEvidence) && (
        <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setShowEvidence(!showEvidence)}
              className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
            >
              {hasEvidence ? `${showEvidence ? 'Hide' : 'Show'} ${d.evidence?.length} linked activity item${d.evidence?.length !== 1 ? 's' : ''}` : 'No linked activity yet'}
            </button>
            <div className="flex gap-2">
              {showAddEvidence && (
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="text-xs px-2 py-1 bg-[var(--color-surface)] rounded hover:bg-[var(--color-border)]"
                >
                  + Add
                </button>
              )}
              <button
                onClick={handleAssess}
                disabled={isAssessing}
                className="text-xs px-2 py-1 bg-purple-900/50 text-purple-300 rounded hover:bg-purple-900/70 disabled:opacity-50"
              >
                {isAssessing ? '🤖 Assessing...' : '🤖 Assess'}
              </button>
            </div>
          </div>

          {/* Add Evidence Form */}
          {showAddForm && (
            <div className="mb-3 p-3 bg-[var(--color-surface)] rounded">
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Title (e.g., draft_report.pdf)"
                  value={newEvidence.title}
                  onChange={(e) => setNewEvidence(prev => ({ ...prev, title: e.target.value }))}
                  className="input w-full text-sm"
                />
                <input
                  type="text"
                  placeholder="Path or URL (optional)"
                  value={newEvidence.path}
                  onChange={(e) => setNewEvidence(prev => ({ ...prev, path: e.target.value }))}
                  className="input w-full text-sm"
                />
                <div className="flex gap-2">
                  <select
                    value={newEvidence.evidence_type}
                    onChange={(e) => setNewEvidence(prev => ({ ...prev, evidence_type: e.target.value as any }))}
                    className="input text-sm"
                  >
                    <option value="file">File</option>
                    <option value="link">Link</option>
                    <option value="commit">Commit</option>
                    <option value="activity">Activity</option>
                  </select>
                  <select
                    value={newEvidence.relationship}
                    onChange={(e) => setNewEvidence(prev => ({ ...prev, relationship: e.target.value as any }))}
                    className="input text-sm"
                  >
                    <option value="contributes">Contributes</option>
                    <option value="completes">Completes</option>
                  </select>
                  <button
                    onClick={() => addEvidenceMutation.mutate(newEvidence)}
                    disabled={!newEvidence.title || addEvidenceMutation.isPending}
                    className="btn-primary text-sm"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Activity List */}
          {showEvidence && hasEvidence && (
            <div className="space-y-1">
              {d.evidence?.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-sm py-1 group">
                  <EvidenceTypeIcon type={e.evidence_type} />
                  <span className="truncate flex-1">{e.title}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    e.relationship === 'completes'
                      ? 'bg-green-900/50 text-green-300'
                      : 'bg-gray-800 text-gray-400'
                  }`}>
                    {e.relationship}
                  </span>
                  <button
                    onClick={() => removeEvidenceMutation.mutate(e.id)}
                    className="opacity-0 group-hover:opacity-100 text-xs px-1.5 py-0.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-opacity"
                    title="Unlink from deliverable"
                    disabled={removeEvidenceMutation.isPending}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DeliverableCard
